import { useI18n } from '../../i18n'
import { formatTimestamp } from '../../lib/youtube/timeFormat'
import styles from './AnchorControls.module.css'

type Props = {
  startSeconds: number
  endSeconds: number
  startText: string
  endText: string
  onStartTextChange: (value: string) => void
  onEndTextChange: (value: string) => void
  onApplyStartText: () => void
  onApplyEndText: () => void
  onSetStartHere: () => void
  onSetEndHere: () => void
  onJumpStart: () => void
  onJumpEnd: () => void
  onSeek: (delta: number) => void
  onPlayPause: () => void
  onPreview: () => void
  onClear?: () => void
  clearDisabled?: boolean
  isPlaying: boolean
  playerReady: boolean
  startError?: string | null
  endError?: string | null
}

export function AnchorControls({
  startSeconds,
  endSeconds,
  startText,
  endText,
  onStartTextChange,
  onEndTextChange,
  onApplyStartText,
  onApplyEndText,
  onSetStartHere,
  onSetEndHere,
  onJumpStart,
  onJumpEnd,
  onSeek,
  onPlayPause,
  onPreview,
  onClear,
  clearDisabled = false,
  isPlaying,
  playerReady,
  startError,
  endError,
}: Props) {
  const { t } = useI18n()

  return (
    <div className={styles.root}>
      <div className={styles.fields}>
        <div className="field">
          <label htmlFor="anchor-start">{t('common.start')}</label>
          <div className={styles.fieldRow}>
            <input
              id="anchor-start"
              inputMode="decimal"
              value={startText}
              onChange={(e) => onStartTextChange(e.target.value)}
              onBlur={onApplyStartText}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onApplyStartText()
              }}
              aria-invalid={Boolean(startError)}
              aria-describedby={startError ? 'anchor-start-error' : 'anchor-start-hint'}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onSetStartHere}
              disabled={!playerReady}
            >
              {t('controls.setStartHere')}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onJumpStart}
              disabled={!playerReady}
            >
              {t('controls.jumpStart')}
            </button>
          </div>
          {startError ? (
            <span id="anchor-start-error" className="field-error">
              {startError}
            </span>
          ) : (
            <span id="anchor-start-hint" className="field-hint timestamp">
              {formatTimestamp(startSeconds)}
            </span>
          )}
        </div>
        <div className="field">
          <label htmlFor="anchor-end">{t('common.end')}</label>
          <div className={styles.fieldRow}>
            <input
              id="anchor-end"
              inputMode="decimal"
              value={endText}
              onChange={(e) => onEndTextChange(e.target.value)}
              onBlur={onApplyEndText}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onApplyEndText()
              }}
              aria-invalid={Boolean(endError)}
              aria-describedby={endError ? 'anchor-end-error' : 'anchor-end-hint'}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onSetEndHere}
              disabled={!playerReady}
            >
              {t('controls.setEndHere')}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onJumpEnd}
              disabled={!playerReady}
            >
              {t('controls.jumpEnd')}
            </button>
          </div>
          {endError ? (
            <span id="anchor-end-error" className="field-error">
              {endError}
            </span>
          ) : (
            <span id="anchor-end-hint" className="field-hint timestamp">
              {formatTimestamp(endSeconds)}
            </span>
          )}
        </div>
      </div>

      <div className={styles.transport} role="group" aria-label={t('controls.seekGroup')}>
        <button type="button" className="btn btn-secondary" onClick={() => onSeek(-5)} disabled={!playerReady}>
          −5s
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => onSeek(-1)} disabled={!playerReady}>
          −1s
        </button>
        <button type="button" className={`btn btn-primary ${styles.play}`} onClick={onPlayPause} disabled={!playerReady}>
          {isPlaying ? t('common.pause') : t('common.play')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => onSeek(1)} disabled={!playerReady}>
          +1s
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => onSeek(5)} disabled={!playerReady}>
          +5s
        </button>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={`btn btn-primary ${styles.preview}`}
          onClick={onPreview}
          disabled={!playerReady}
        >
          {t('controls.previewSegment')}
        </button>
        {onClear && (
          <button type="button" className="btn btn-danger" onClick={onClear} disabled={clearDisabled}>
            {t('common.clear')}
          </button>
        )}
      </div>
    </div>
  )
}
